import { parseLocation } from 'parse-address'
import API from '../API'
import deepEqual from 'deep-equal'
const propertiesSelect = [
  'geometry',
  'mlsId',
  'listPrice',
  'mls.status',
  'geo.lat',
  'geo.lng',
  'photos',
  'property.bedrooms',
  'property.bathrooms',
  'property.bathsFull',
  'property.bathsHalf',
  'property.area',
  'property.type',
  'office',
  'address',
  'url',
  'listingId',
  'vendor',
  'listDate'
]
const soldPropertiesSelect = [
  'geometry',
  'mlsId',
  'listPrice',
  'mls.status',
  'geo.lat',
  'geo.lng',
  'photos',
  'property.bedrooms',
  'property.bathrooms',
  'property.bathsFull',
  'property.bathsHalf',
  'property.area',
  'property.type',
  'office',
  'address',
  'url',
  'listingId',
  'vendor',
  'sales.closeDate',
  'sales.closePrice',
  'mls.daysOnMarket',
  'listDate'
]

export default class PropertyStore {
  constructor(
    boundary,
    status = 'Active,ActiveUnderContract',
    system = 'postgres'
  ) {
    this.loading = false
    this.finished = false
    this.started = false
    this.status = status.split(',').map((s) => s.trim())
    this.properties = []
    this.subscribers = []
    this.finishFuncs = []
    this.mlsFuncs = []
    this.geometry = null
    this.boundary = boundary
    this.geometryCB = null
    this.mls = null
    this.retryCount = 0
    this.limit = status.indexOf('Closed') === -1 ? 200 : 50
    this.system = system
    this.minimumHomePrice = 15000
  }

  setTypes = (types) => {
    this.types = types
  }

  setSubTypes = (subTypes) => {
    this.subTypes = subTypes
  }

  setMinimumHomePrice = (minPrice) => {
    this.minimumHomePrice = minPrice
  }

  setGeometry = (geometry) => {
    this.geometry = geometry
    if (this.geometryCB) {
      this.geometryCB()
    }
  }

  setMls = (mls) => {
    this.mls = mls
    this.mlsFuncs.forEach((f) => f())
    if (this.geometryCB) {
      this.geometryCB()
    }
  }

  subscribe = (func, done) => {
    // If we haven't started getting properties yet, lets start
    if (!this.loading && !this.finished) {
      this.getProperties()
    }
    this.subscribers.push(func)
    if (done) this.finishFuncs.push(done)
    if (this.started) func(this.properties)
    if (!this.loading && this.finished && done) {
      done(this.properties)
    }
  }

  subscribeMls = (func) => {
    this.mlsFuncs.push(func)
  }

  addProperties = (properties) => {
    this.properties =
      this.system !== 'mongo'
        ? [...this.properties, ...properties]
        : [
            ...this.properties,
            ...properties.map((p) => {
              return { ...p, parsedAddress: parseLocation(p.address.full) }
            })
          ].filter(
            (p, i, arr) =>
              !arr.find(
                (print) =>
                  print.parsedAddress &&
                  this.mls.indexOf(print.vendor) < this.mls.indexOf(p.vendor) &&
                  deepEqual(print.parsedAddress, p.parsedAddress) &&
                  print.vendor !== p.vendor
              )
          )

    this.subscribers.forEach((func) => func(this.properties))
  }

  getProperties = async () => {
    this.loading = true
    let properties = []
    let offset = 0
    if (!this.geometry || !this.mls) {
      this.geometryCB = this.getProperties
      return
    }
    do {
      properties = await this.getPage(offset)
      this.retryCount = 0
      this.addProperties(properties)
      offset += 1
      this.started = true
    } while (
      properties.length >= this.limit &&
      (this.status.indexOf('Closed') !== -1 ? offset < 1 : true)
    )
    this.finishFuncs.forEach((f) => f(this.properties))
    this.loading = false
    this.finished = true
  }

  postgresProperties = async (
    neighborhood,
    offset,
    select,
    mls,
    status,
    limit,
    types,
    subTypes,
    minimumHomePrice
  ) => {
    try {
      const res = await fetch(
        `https://${process.env.REACT_APP_API_ENDPOINT}/v1/simply-rets-service/postgres`,
        {
          mode: 'cors',
          method: 'POST',
          body: JSON.stringify({
            boundary: this.boundary,
            offset: offset,
            mls: mls,
            status: status,
            limit: limit,
            types: types,
            subTypes: subTypes,
            minPrice: minimumHomePrice
          })
        }
      )
      if (res) return res.json()
      else return {}
    } catch (e) {
      console.error(e)
    }
  }

  async getPage(offset) {
    try {
      const api =
        this.system === 'postgres'
          ? this.postgresProperties
          : API.availableProperties.bind(API)
      return (
        (await api(
          { geometry: this.geometry },
          offset,
          this.status.indexOf('Closed') === -1
            ? propertiesSelect
            : soldPropertiesSelect,
          this.mls,
          this.status,
          this.limit,
          this.types,
          this.subTypes,
          this.minimumHomePrice
        )) || []
      )
    } catch (e) {
      console.error(e)
      this.retryCount += 1
      if (this.retryCount <= 5) {
        return this.getPage(offset)
      } else {
        throw e
      }
    }
  }
}
